// One recompute, from what was counted to what goes on the purchase order.
//
// The chain has ten links and the bug this module exists to remove is that they
// were computed in six different places, at six different times, from three
// different fallback rules. A count could change and the order quantity beside
// it would not, because the order quantity had been calculated at session load
// from `par_level − actual_quantity` and nothing recalled it.
//
//   entry → row on-hand → item on-hand → PAR → need
//         → packages → received → overage → extended cost → draft staleness
//
// Everything is derived here in one pass from the raw inputs. Nothing reads a
// previously stored order quantity as an input, so there is no state to go
// stale; a caller that wants the old number has to keep it deliberately, which
// is what a manual override is.
//
// The one thing this module will not do is fill a gap with a zero. A need it
// cannot compute is null and says why. An order line for an item whose package
// size nobody confirmed comes back with the minimum, a null overage and a
// reason — never "order 0", which reads as "we have enough".
//
// Pure ESM. No Deno APIs, no I/O.

import { normalizeCountEntries, combineLocationTotals } from "./countEntryMath.mjs";
import {
  resolveRowPar,
  resolveMoqPackages,
  orderPackagesForNeed,
  computePurchaseTarget,
  computeOrderPlan,
  PAR_STATUS,
  MOQ_SOURCES,
} from "./parAndMoq.mjs";
import { interpretPackage } from "./packageLadder.mjs";
import { resolveNeedToPackageBasis, validatePackageConsistency } from "./packageBasis.mjs";
import { normalizeUnit } from "../menu-recipe-costing/units.mjs";

const round6 = (n) => Math.round(n * 1e6) / 1e6;

const text = (v) => (v == null ? "" : String(v).trim());

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const finiteNonNegative = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Why an order quantity could not be produced. */
export const ORDER_STATUS = Object.freeze({
  READY: "ready",
  NO_OPTION: "no_option",
  UNRESOLVED_PACKAGE: "unresolved_package",
  UNKNOWN_NEED: "unknown_need",
  SATISFIED: "satisfied",
});

export const ORDER_STATUS_TEXT = Object.freeze({
  no_option: "Choose a vendor product to see how many packages to order.",
  unresolved_package: "This vendor package has no confirmed size, so the number of packages is a minimum, not a calculation.",
  unknown_need: "The shortage is unknown, so no order quantity can be calculated.",
  satisfied: "On hand meets the PAR.",
});

/**
 * On-hand for ONE count line.
 *
 * Prefers the raw entries, because they are the auditable record of what was
 * physically seen. Falls back to the line's stored `actual_quantity` for every
 * legacy row, which has no entries and whose number is not therefore missing —
 * it was just counted the old way.
 *
 * @returns {{ total, source:'entries'|'stored'|'uncounted', hasUnresolved, warnings }}
 */
export function rowOnHand({ row, entries, countUnit, options, vendorNamesById, frozen } = {}) {
  const list = entries || [];
  // A row belonging to a closed session reads back exactly what was stored.
  // snapshot_at_close is the existing marker that a count is history.
  const isFrozen = frozen ?? (row?.snapshot_at_close != null);
  if (list.length > 0) {
    const normalized = normalizeCountEntries({
      entries: list,
      countUnit,
      options,
      vendorNamesById,
      frozen: isFrozen,
    });
    return {
      total: normalized.total,
      source: "entries",
      hasUnresolved: normalized.hasUnresolved,
      resolved: normalized.resolved,
      unresolved: normalized.unresolved,
      warnings: normalized.warnings,
    };
  }

  // is_counted is the only thing that distinguishes "counted as none" from
  // "not reached yet". Both store actual_quantity 0 or null, and treating the
  // second as a real zero is how an uncounted walk-in becomes an order for a
  // full PAR of stock that is already on the shelf.
  const stored = finiteNonNegative(row?.actual_quantity ?? row?.current_count);
  if (row?.is_counted && stored !== null) {
    return { total: stored, source: "stored", hasUnresolved: false, resolved: [], unresolved: [], warnings: [] };
  }

  return { total: null, source: "uncounted", hasUnresolved: false, resolved: [], unresolved: [], warnings: [] };
}

/**
 * The item-global PAR across sibling location rows.
 *
 * PAR is a property of the item, and the rows should agree. Legacy templates
 * sometimes disagree, so the largest resolvable normalized PAR wins — the same
 * rule applySharedOrderMathBulk has always used, kept so this does not quietly
 * change existing orders. A row whose PAR cannot be normalized is reported, not
 * silently skipped: "2 cases" against a gallon count unit is a PAR the user
 * still has to fix.
 */
export function itemPar({ rows, countUnit, options } = {}) {
  let best = null;
  const unresolvedRows = [];

  (rows || []).forEach((row) => {
    const resolution = resolveRowPar(row, { countUnit, options });
    if (resolution.status === PAR_STATUS.VALID && resolution.normalized != null) {
      if (!best || resolution.normalized > best.normalized) best = resolution;
    } else if (resolution.status === PAR_STATUS.UNRESOLVED || resolution.status === PAR_STATUS.REQUIRES_OPTION) {
      unresolvedRows.push({ rowId: row?.id ?? null, reason: resolution.reason, reasonText: resolution.reasonText });
    }
  });

  if (best) return { ...best, unresolvedRows };
  return {
    status: unresolvedRows.length > 0 ? PAR_STATUS.UNRESOLVED : PAR_STATUS.EMPTY,
    normalized: null,
    normalizedUnit: normalizeUnit(countUnit).key || null,
    display: null,
    reason: unresolvedRows[0]?.reason ?? null,
    reasonText: unresolvedRows[0]?.reasonText ?? null,
    snapshot: null,
    warnings: [],
    unresolvedRows,
  };
}

/**
 * The whole chain for one canonical item, from its count rows to its PO line.
 *
 * @param {object} args
 * @param {Array}  args.rows              every count_items row for this item, all locations
 * @param {Object} [args.entriesByRow]    { [count_item_id]: entries[] }
 * @param {Array}  [args.options]         purchase_options rows
 * @param {string} args.countUnit
 * @param {*}      [args.selectedOptionId] the ORDER option — not the count format
 * @param {*}      [args.legacyMoq]       inventory_items.moq, last-resort only
 * @param {number} [args.manualPackages]  a human's override, in packages
 * @param {Object} [args.item]            inventory_items row (or subset) carrying
 *                                        replenishment_minimum_qty / replenishment_increment_qty
 *                                        (count unit); falls back to a row's item_snapshot
 * @returns {object} every intermediate value, each labeled with its unit
 */
export function recomputeItemOrder({
  rows,
  entriesByRow,
  options,
  countUnit,
  selectedOptionId,
  legacyMoq,
  manualPackages,
  vendorNamesById,
  frozen,
  item,
} = {}) {
  const rowList = (rows || []).filter(Boolean);
  const optionList = (options || []).filter(Boolean);
  const unit = normalizeUnit(countUnit).key || text(countUnit) || null;
  const warnings = [];

  // 1–2. Each location's on-hand, from its entries.
  const locations = rowList.map((row) => {
    const entries = (entriesByRow || {})[String(row.id)] || [];
    const result = rowOnHand({ row, entries, countUnit, options: optionList, vendorNamesById, frozen });
    warnings.push(...result.warnings);
    return {
      rowId: row.id ?? null,
      location: row.location ?? row.item_snapshot?.location ?? null,
      total: result.total,
      source: result.source,
      hasUnresolved: result.hasUnresolved,
      entryCount: entries.length,
    };
  });

  // 3. Combined on-hand. Locations not yet reached contribute nothing and are
  //    counted separately so the caller can say the total is partial.
  //
  //    The related-group fold (browser utils/countGroupFold) hides sibling
  //    members' rows and stamps their typed counts onto the favored member's
  //    first row as group_baseline_count (null when no hidden row was
  //    counted — absence of evidence is not a zero). The baseline joins the
  //    on-hand total here exactly as it does in applySharedOrderMathBulk and
  //    buildNeedsFromCountItems, so the five need implementations agree.
  const contributing = locations.filter((l) => l.source !== "uncounted");
  const combined = combineLocationTotals(contributing);
  let groupBaseline = null;
  rowList.forEach((row) => {
    const baseline = finiteNonNegative(row?.group_baseline_count);
    if (baseline !== null) groupBaseline = (groupBaseline ?? 0) + baseline;
  });
  const onHand = combined.total == null
    ? (groupBaseline == null ? null : round6(groupBaseline))
    : round6(combined.total + (groupBaseline ?? 0));
  const uncountedLocations = locations.filter((l) => l.source === "uncounted").length;

  // 4. PAR, item-global and vendor-neutral. The fold's group_par_floor (max
  //    hidden-member PAR, already in the count unit — the fold only folds
  //    unit-compatible members) joins the normalize-to-max rule.
  const par = itemPar({ rows: rowList, countUnit, options: optionList });
  let groupParFloor = null;
  rowList.forEach((row) => {
    const floor = positive(row?.group_par_floor);
    if (floor !== null && (groupParFloor === null || floor > groupParFloor)) groupParFloor = floor;
    // A hidden sibling stated a PAR the fold's normalized resolution refused
    // ("2 cases", no package) — report it exactly like an unresolvable PAR on
    // a visible row instead of letting the group's PAR quietly understate.
    if (row?.group_par_unresolved) {
      par.unresolvedRows = [...(par.unresolvedRows || []), {
        rowId: row?.id ?? null,
        reason: "unresolved_package",
        reasonText: "A related item's PAR needs its package confirmed before it can join this group's target.",
      }];
    }
  });
  const parNormalized = groupParFloor !== null && (par.normalized == null || groupParFloor > par.normalized)
    ? groupParFloor
    : par.normalized;

  // 5. Need.
  let need = null;
  let needReason = null;
  if (parNormalized == null) {
    needReason = "no_par";
  } else if (onHand == null) {
    needReason = "not_counted";
  } else {
    need = round6(Math.max(0, parNormalized - onHand));
  }

  // 5b. Item replenishment stage: the restaurant's own purchase target. The
  //     minimum is a FLOOR, the increment a STEP, both in the count unit. The
  //     NEED above stays the raw shortage — a fact about the shelf — and only
  //     the package arithmetic below pursues the target.
  const itemRules = item ?? rowList.find((r) => r?.item_snapshot)?.item_snapshot ?? null;
  const targeting = computePurchaseTarget({
    rawNeed: need,
    itemMinimum: itemRules?.replenishment_minimum_qty,
    itemIncrement: itemRules?.replenishment_increment_qty,
  });
  const purchaseTarget = need == null ? null : targeting.target;

  // 6–9. Packages, in the selected vendor's own order unit.
  const selectedOption =
    selectedOptionId == null
      ? null
      : optionList.find((o) => String(o.id) === String(selectedOptionId)) || null;
  if (selectedOptionId != null && !selectedOption) {
    warnings.push("The selected vendor product is not in this item's option list.");
  }

  const order = orderForOption({
    need: purchaseTarget ?? need,
    option: selectedOption,
    options: optionList,
    countUnit,
    legacyMoq,
  });

  // The one decisive constraint, named — the same layered plan the confirm
  // trigger freezes. Computed via computeOrderPlan with the identical inputs
  // orderForOption used, so the packages figures cannot disagree.
  const plan = selectedOption && need != null
    ? computeOrderPlan({
      rawNeed: need,
      itemMinimum: itemRules?.replenishment_minimum_qty,
      itemIncrement: itemRules?.replenishment_increment_qty,
      qtyPerPackage: order.qtyPerPackage,
      moqPackages: order.moqSource === MOQ_SOURCES.DEFAULT ? null : order.moq,
      allowsFractional: order.allowsFractional,
      orderIncrement: order.orderIncrement,
      packagePrice: selectedOption.package_price,
    })
    : null;

  // A human's override replaces the package count and everything downstream of
  // it, but never the need — the shortage is a fact about the shelf and is not
  // the user's to overrule.
  const manual = finiteNonNegative(manualPackages);
  const effectivePackages = manual !== null ? manual : order.packages;
  const perPackage = order.qtyPerPackage;
  const quantityReceived =
    effectivePackages != null && perPackage != null ? round6(effectivePackages * perPackage) : null;
  const overage =
    quantityReceived != null && need != null ? round6(quantityReceived - need) : null;
  const packagePrice = positive(selectedOption?.package_price);
  const extendedCost =
    effectivePackages != null && packagePrice != null ? round6(effectivePackages * packagePrice) : null;

  return {
    countUnit: unit,
    locations,
    onHand,
    onHandPartial: combined.partial || uncountedLocations > 0,
    uncountedLocations,
    countedLocations: contributing.length,
    hasUnresolvedEntries: locations.some((l) => l.hasUnresolved),

    par: parNormalized,
    parUnit: par.normalizedUnit || unit,
    parStatus: parNormalized != null && par.status !== PAR_STATUS.VALID ? PAR_STATUS.VALID : par.status,
    parDisplay: par.display ?? (groupParFloor !== null ? String(groupParFloor) : null),
    parReasonText: par.reasonText,

    need,
    needReason,

    // The item-replenishment stage, stated beside the raw need it shaped.
    // orderDriver describes the CALCULATED packages (a manual override is
    // reported by isManualOverride, not disguised as a driver).
    purchaseTarget,
    targetDriver: need == null ? null : targeting.driver,
    orderDriver: plan?.driver ?? null,

    selectedOptionId: selectedOption?.id ?? null,
    orderUnit: order.orderUnit,
    qtyPerPackage: perPackage,
    moq: order.moq,
    packages: effectivePackages,
    calculatedPackages: order.packages,
    isManualOverride: manual !== null,
    quantityReceived,
    overage,
    packagePrice,
    extendedCost,
    allowsFractional: order.allowsFractional,
    orderIncrement: order.orderIncrement ?? null,

    status: manual !== null && order.status === ORDER_STATUS.UNKNOWN_NEED ? ORDER_STATUS.READY : order.status,
    statusText: ORDER_STATUS_TEXT[order.status] || null,
    warnings: [...warnings, ...order.warnings],
  };
}

/**
 * Packages for a need against one specific vendor product.
 *
 * Split out because "how many of this vendor's packages" is exactly the
 * question the order-review screen re-asks every time the user switches vendor,
 * and it must not require re-deriving the count.
 */
export function orderForOption({ need, option, options, countUnit, legacyMoq } = {}) {
  const warnings = [];
  const moqResolution = resolveMoqPackages({
    selectedOption: option,
    options: options || [],
    legacyMoq,
  });

  if (!option) {
    return {
      packages: null,
      qtyPerPackage: null,
      quantityReceived: null,
      overage: null,
      orderUnit: null,
      moq: moqResolution.packages,
      allowsFractional: false,
      status: need == null ? ORDER_STATUS.UNKNOWN_NEED : ORDER_STATUS.NO_OPTION,
      warnings,
    };
  }

  const interp = interpretPackage(option, countUnit);
  const qtyPerPackage = positive(interp.quantity_in_item_count_unit);
  const allowsFractional = option.allows_fractional_packages === true;
  const orderIncrement = positive(option.order_increment);
  const orderUnit = interp.order_unit || text(option.purchase_unit) || "package";

  // resolveMoqPackages falls back to 1 package when nobody stated a minimum.
  // Forwarding that as a stated MOQ would clamp a cut-to-order vendor's 0.7 lb
  // up to a whole pound, defeating the fractional exception — orderPackagesForNeed
  // applies its own implicit minimum, and it knows which kind of vendor this is.
  const statedMoq = moqResolution.source === MOQ_SOURCES.DEFAULT ? null : moqResolution.packages;

  const result = orderPackagesForNeed({
    need,
    qtyPerPackage,
    moqPackages: statedMoq,
    allowsFractional,
    orderIncrement,
  });

  let status = ORDER_STATUS.READY;
  if (need == null) status = ORDER_STATUS.UNKNOWN_NEED;
  else if (result.reason === "unresolved_package") status = ORDER_STATUS.UNRESOLVED_PACKAGE;
  else if (need <= 0) status = ORDER_STATUS.SATISFIED;

  if (qtyPerPackage === null) {
    warnings.push(
      `The size of one ${orderUnit} is not confirmed, so ${result.packages ?? "the minimum"} is a minimum rather than a calculated quantity.`,
    );
  }

  return {
    packages: result.packages,
    qtyPerPackage,
    quantityReceived: result.quantityReceived,
    overage: result.overage,
    orderUnit,
    moq: moqResolution.packages,
    moqSource: moqResolution.source,
    allowsFractional,
    orderIncrement,
    status,
    warnings,
  };
}

/* ── Staleness ────────────────────────────────────────────────────────────── */

/**
 * Whether a saved draft line still matches what the counts now say.
 *
 * Compared on the inputs a person would recognise as "the count changed" —
 * on-hand, PAR, the chosen vendor — plus the resulting package count. Comparing
 * only the package count would call a draft fresh when a count changed but
 * rounding absorbed it, and the reviewer would then approve a line whose stated
 * on-hand is wrong.
 *
 * A sent PO is never stale: it is a promise already made, and amendments are a
 * different flow.
 *
 * @returns {{ stale:boolean, changed:string[] }}
 */
export function allocationIsStale(saved, computed) {
  if (!saved || !computed) return { stale: false, changed: [] };
  if (text(saved.status) === "sent" || text(saved.status) === "received") {
    return { stale: false, changed: [] };
  }

  const changed = [];
  const differs = (a, b) => {
    const x = a == null ? null : Number(a);
    const y = b == null ? null : Number(b);
    if (x === null && y === null) return false;
    if (x === null || y === null) return true;
    return Math.abs(x - y) > 1e-6;
  };

  if (differs(saved.on_hand_quantity ?? saved.onHand, computed.onHand)) changed.push("on hand");
  if (differs(saved.par_quantity ?? saved.par, computed.par)) changed.push("PAR");
  if (differs(saved.needed_quantity ?? saved.need, computed.need)) changed.push("need");
  if (differs(saved.packages ?? saved.package_quantity, computed.packages)) changed.push("packages");

  const savedOption = saved.purchase_option_id ?? saved.selectedOptionId ?? null;
  const computedOption = computed.selectedOptionId ?? null;
  if (String(savedOption ?? "") !== String(computedOption ?? "")) changed.push("vendor");

  return { stale: changed.length > 0, changed };
}

/* ── Display ──────────────────────────────────────────────────────────────── */

/**
 * The order line in words, every number carrying its unit.
 *
 * "Order 4" is the display this replaces. Four what — cases, pounds, ounces?
 * The number alone has been the source of every wrong-by-a-case order, so
 * nothing here emits a bare quantity.
 */
export function describeOrderLine(computed, { itemName, vendorLabel } = {}) {
  if (!computed) return null;
  const unit = computed.countUnit || "";
  const qty = (v) => (v == null ? "—" : `${round6(v)}${unit ? ` ${unit}` : ""}`);
  const pkgs = (v) => {
    if (v == null) return "—";
    const noun = computed.orderUnit || "package";
    return `${round6(v)} ${v === 1 || noun.endsWith("s") ? noun : `${noun}s`}`;
  };

  return {
    itemName: text(itemName) || null,
    vendorLabel: text(vendorLabel) || null,
    onHand: qty(computed.onHand),
    par: qty(computed.par),
    need: qty(computed.need),
    vendorPackage: computed.qtyPerPackage != null ? `${round6(computed.qtyPerPackage)} ${unit} per ${computed.orderUnit || "package"}` : null,
    order: pkgs(computed.packages),
    quantityReceived: qty(computed.quantityReceived),
    overage: computed.overage == null ? null : `${round6(computed.overage)}${unit ? ` ${unit}` : ""} over`,
    packagePrice:
      computed.packagePrice == null
        ? null
        : `$${computed.packagePrice.toFixed(2)} per ${computed.orderUnit || "package"}`,
    extendedCost: computed.extendedCost == null ? null : `$${computed.extendedCost.toFixed(2)}`,
    statusText: computed.statusText,
  };
}

/* ── Direct order: a typed quantity against one vendor product ────────────── */

/**
 * The direct-order/count-to-PO leg of the chain: a human typed the quantity
 * they WANT (manual_order_quantity, in the item's own count unit), a purchase
 * option has been resolved for the line, and the order must leave in the
 * vendor's terms under the vendor's rules — same package basis, same MOQ,
 * same increment, same fractional rule as Savings Review, from the same two
 * owners (packageBasis resolves the package, orderPackagesForNeed does the
 * math).
 *
 * Deliberately conservative: anything short of a fully coherent answer —
 * inactive option, conflicting package facts, an incompatible need unit, an
 * unpriced package (catch-weight rows price by weight, not here) — returns
 * `{ applicable: false, reason }` and the caller keeps its legacy verbatim
 * line, with NO purchase_option_id and NO invented minimum. The typed value
 * is never silently reduced; only raised to the nearest legal buy, with the
 * adjustment named.
 *
 * @param {object} args
 * @param {number} args.typedQuantity  what the buyer asked for, in needUnit
 * @param {string} args.needUnit       the item's count unit (the line's unit today)
 * @param {object} args.option         a live purchase_options row
 * @returns {{ applicable:boolean, reason:string|null,
 *             packages:number|null, quantityReceived:number|null,
 *             overage:number|null, unit:string|null, packSize:string|null,
 *             packagePrice:number|null, lineTotal:number|null,
 *             adjusted:boolean }}
 */
export function planTypedOrderLine({ typedQuantity, needUnit, option } = {}) {
  const no = (reason) => ({
    applicable: false, reason,
    packages: null, quantityReceived: null, overage: null,
    unit: null, packSize: null, packagePrice: null, lineTotal: null,
    adjusted: false,
  });

  const typed = positive(typedQuantity);
  if (typed === null) return no("no_quantity");
  if (!option) return no("no_option");
  if (option.status !== "active") return no("option_inactive");

  const readinessItem = { unit: text(needUnit) || null };
  const consistency = validatePackageConsistency(option, { item: readinessItem });
  if (!consistency.orderReady) return no("package_not_order_ready");

  const basis = text(needUnit)
    ? resolveNeedToPackageBasis({
      needQty: typed,
      needUnit,
      option,
      packageBasis: consistency.basis,
      item: readinessItem,
    })
    : null;
  if (basis && !basis.compatible) return no("need_unit_incompatible");
  const per = positive(basis?.quantityPerPackageInNeedUnit ?? consistency.basis.quantityInInventoryUnit);
  if (per === null) return no("unresolved_package");

  const packagePrice = positive(option.package_price);
  if (packagePrice === null) return no("no_package_price");

  const plan = orderPackagesForNeed({
    need: typed,
    qtyPerPackage: per,
    moqPackages: option.moq_packages,
    allowsFractional: option.allows_fractional_packages === true,
    orderIncrement: option.order_increment,
  });
  if (plan.reason || plan.packages == null || plan.packages <= 0) {
    return no(plan.reason || "no_packages");
  }

  return {
    applicable: true,
    reason: null,
    packages: plan.packages,
    quantityReceived: plan.quantityReceived,
    overage: plan.overage,
    unit: text(option.purchase_unit) || "package",
    packSize: text(option.pack_description) || null,
    packagePrice,
    lineTotal: Math.round(plan.packages * packagePrice * 100) / 100,
    adjusted: plan.quantityReceived != null && Math.abs(plan.quantityReceived - typed) > 1e-9,
  };
}
